import { useCallback, useMemo, useState } from 'react'
import { Button } from '@saas-pages/shared/ui'
import { toolError, toolInvoke, toolSuccess } from '@saas-pages/shared/public/runtime'
import type { Plugin } from 'prettier'
import type { MinifyOptions } from 'terser'
import { numberLocale, useLocale, useT } from '@/shared/i18n'
import { ToolPageShell } from '@/shared/tool-shell/ToolPageShell'
import { CodeWorkspace } from '@/shared/ui/code-workspace'

const UTILITY = 'javascript-formatter'

type JavaScriptResult = { ok: true; result: string } | { ok: false; error: string }

const SCRIPT_MINIFY_OPTIONS: MinifyOptions = {
  compress: true,
  mangle: true,
  format: {
    comments: false,
  },
}

const MODULE_MINIFY_OPTIONS: MinifyOptions = {
  ...SCRIPT_MINIFY_OPTIONS,
  module: true,
}

interface ParserLocation {
  line?: number
  column?: number
  col?: number
}

interface JavaScriptIssue extends Error {
  loc?: ParserLocation
  line?: number
  column?: number
  col?: number
}

async function loadJavaScriptFormatter() {
  const [prettier, babelPlugin, estreePlugin] = await Promise.all([
    import('prettier/standalone'),
    import('prettier/plugins/babel'),
    import('prettier/plugins/estree'),
  ])

  return {
    prettier,
    plugins: [babelPlugin, estreePlugin] as Plugin[],
  }
}

function formatJavaScriptIssue(error: unknown): string {
  if (!(error instanceof Error)) return String(error)

  const issue = error as JavaScriptIssue
  const line = issue.loc?.line ?? issue.line
  const column = issue.loc?.column ?? issue.loc?.col ?? issue.column ?? issue.col
  if (line && typeof column === 'number') {
    return `line ${line}, column ${column + 1}: ${error.message}`
  }

  return error.message
}

async function formatJavaScript(input: string): Promise<JavaScriptResult> {
  if (!input.trim()) return { ok: false, error: 'empty' }

  try {
    const { prettier, plugins } = await loadJavaScriptFormatter()
    const result = await prettier.format(input, {
      parser: 'babel',
      plugins,
      printWidth: 100,
      tabWidth: 2,
      singleQuote: true,
      semi: true,
    })

    return { ok: true, result: result.trimEnd() }
  } catch (error) {
    return { ok: false, error: formatJavaScriptIssue(error) }
  }
}

async function minifyJavaScript(input: string): Promise<JavaScriptResult> {
  if (!input.trim()) return { ok: false, error: 'empty' }

  try {
    const { minify } = await import('terser')
    let result
    try {
      result = await minify(input, SCRIPT_MINIFY_OPTIONS)
    } catch {
      result = await minify(input, MODULE_MINIFY_OPTIONS)
    }

    return { ok: true, result: result.code ?? '' }
  } catch (error) {
    return { ok: false, error: formatJavaScriptIssue(error) }
  }
}

export function JavaScriptFormatterPage() {
  const t = useT()
  const { locale } = useLocale()
  const localeCode = numberLocale(locale)
  const [input, setInput] = useState('')
  const [output, setOutput] = useState('')
  const [error, setError] = useState<string | null>(null)
  const [statusMessage, setStatusMessage] = useState<string | null>(null)
  const [isProcessing, setIsProcessing] = useState(false)
  const [copied, setCopied] = useState(false)

  const inputStats = useMemo(() => {
    const chars = input.length
    const lines = input ? input.split('\n').length : 0
    return { chars, lines }
  }, [input])

  const outputStats = useMemo(() => {
    const chars = output.length
    const lines = output ? output.split('\n').length : 0
    return { chars, lines }
  }, [output])

  const processJavaScript = useCallback(async (action: 'format' | 'minify') => {
    if (!input.trim()) {
      setOutput('')
      setError(null)
      setStatusMessage(null)
      return
    }

    const start = performance.now()
    setIsProcessing(true)
    setError(null)
    setStatusMessage(null)
    toolInvoke(UTILITY, { action })

    const result = action === 'format'
      ? await formatJavaScript(input)
      : await minifyJavaScript(input)

    if (result.ok) {
      setOutput(result.result)
      setStatusMessage(
        action === 'format' ? t.javascriptFormatter.formatted : t.javascriptFormatter.minified,
      )
      toolSuccess(UTILITY, Math.round(performance.now() - start), { action })
    } else {
      setOutput('')
      setError(t.javascriptFormatter.errors.invalidJavaScript.replace('{error}', result.error))
      toolError(UTILITY, 'invalid-javascript', { action })
    }

    setIsProcessing(false)
  }, [input, t])

  const handleCopy = useCallback(async () => {
    if (!output) return
    try {
      await navigator.clipboard.writeText(output)
      setCopied(true)
      setTimeout(() => setCopied(false), 1500)
    } catch {
      setCopied(false)
    }
  }, [output])

  const handleClear = useCallback(() => {
    setInput('')
    setOutput('')
    setError(null)
    setStatusMessage(null)
  }, [])

  return (
    <ToolPageShell toolKey="javascriptFormatter" width="wide">
      <CodeWorkspace
        error={error}
        notice={statusMessage}
        controls={(
          <div className="flex flex-wrap items-center gap-3">
            <Button
              onClick={() => void processJavaScript('format')}
              disabled={!input.trim() || isProcessing}
            >
              {t.javascriptFormatter.format}
            </Button>
            <Button
              variant="secondary"
              onClick={() => void processJavaScript('minify')}
              disabled={!input.trim() || isProcessing}
            >
              {t.javascriptFormatter.minify}
            </Button>
          </div>
        )}
        input={{
          title: t.javascriptFormatter.input,
          value: input,
          onChange: (value) => {
            setInput(value)
            setError(null)
            setStatusMessage(null)
          },
          placeholder: t.javascriptFormatter.inputPlaceholder,
          ariaLabel: t.javascriptFormatter.input,
          clear: { label: t.javascriptFormatter.clear, onClick: handleClear },
          stats: [
            {
              label: t.javascriptFormatter.characters,
              value: inputStats.chars.toLocaleString(localeCode),
            },
            {
              label: t.javascriptFormatter.lines,
              value: inputStats.lines.toLocaleString(localeCode),
            },
          ],
        }}
        output={{
          title: t.javascriptFormatter.output,
          value: output,
          readOnly: true,
          placeholder: t.javascriptFormatter.outputPlaceholder,
          ariaLabel: t.javascriptFormatter.output,
          actions: (
            <Button variant="outline" size="sm" onClick={handleCopy}>
              {copied ? t.javascriptFormatter.copied : t.javascriptFormatter.copy}
            </Button>
          ),
          stats: output && !error
            ? [
              {
                label: t.javascriptFormatter.characters,
                value: outputStats.chars.toLocaleString(localeCode),
              },
              {
                label: t.javascriptFormatter.lines,
                value: outputStats.lines.toLocaleString(localeCode),
              },
            ]
            : undefined,
        }}
      />

      <section className="mt-10 space-y-5 border-t pt-8">
        <div>
          <h2 className="text-xl font-semibold tracking-normal">
            {t.javascriptFormatter.howTitle}
          </h2>
          <p className="mt-2 text-sm leading-6 text-muted-foreground">
            {t.javascriptFormatter.how}
          </p>
        </div>
        <div>
          <h2 className="text-xl font-semibold tracking-normal">
            {t.javascriptFormatter.whenTitle}
          </h2>
          <p className="mt-2 text-sm leading-6 text-muted-foreground">
            {t.javascriptFormatter.when}
          </p>
        </div>
      </section>
    </ToolPageShell>
  )
}
